/**
 * Panel chrome for the ExceptionDrawer - the AppDrawerShell composition:
 * optional admin context bar, shared header, tab bar, tab bodies (with
 * the loading skeleton) and the footer (Notes composer / "Add note").
 *
 * The outer <Paper> + <Slide> container stays in ExceptionDrawer.tsx -
 * MUI Slide clones its child with a ref, so keeping the Paper literal
 * there sidesteps any forwardRef plumbing. Purely presentational: all
 * state arrives via the drawer's model object.
 */
import React from 'react'
import { Box, Button, Link, Skeleton, Stack } from '@mui/material'
import BalanceIcon from '@mui/icons-material/Balance'
import EditNoteIcon from '@mui/icons-material/EditNote'
import {
  AppDrawerBody,
  AppDrawerFooter,
  AppDrawerHeader,
  AppDrawerShell,
  AppDrawerTabs,
} from '../../../shared/AppDrawer'
import type { AppDrawerTabDef } from '../../../shared/AppDrawer'
import { poolwerxColors } from '../../../../theme/colors'
import SourceLinksRow from '../SourceLinksRow'
import AdminContextBar from './AdminContextBar'
import ActivityPanel from './ActivityPanel'
import NotesPanel from './NotesPanel'
import NotesComposer from './NotesComposer'
import ObjectActionLine from './ObjectActionLine'
import type { useExceptionDrawerModel } from './useExceptionDrawerModel'
import type { DrawerTab, ExceptionDrawerProps } from './props'

interface ExceptionDrawerPanelProps {
  model: ReturnType<typeof useExceptionDrawerModel>
  /**
   * Builds the issues body for a given view (Open or Closed issues tab),
   * deferred so the panel only renders the active tab's list
   * (ExceptionDrawerIssuesBody).
   */
  renderIssuesBody: (view: 'open' | 'closed') => React.ReactNode
  onClose: () => void
  fpContext?: ExceptionDrawerProps['fpContext']
  reportHref?: string
  fpCode: string
  /**
   * Opens the LS<->Xero CompareSalesDialog for the drawer's row. Set by
   * ExceptionDrawer only when the row carries the backend-stamped
   * ``compare_sales`` target; absent = no Compare action rendered.
   */
  onCompareSales?: () => void
}

const ExceptionDrawerPanel: React.FC<ExceptionDrawerPanelProps> = ({
  model,
  renderIssuesBody,
  onClose,
  fpContext,
  reportHref,
  fpCode,
  onCompareSales,
}) => {
  const {
    user,
    notesGated,
    actionsGated,
    notes,
    objectActions,
    bodyReady,
    activeTab,
    handleTabChange,
    displayEvents,
    lastConfirmedAt,
    historyLoading,
    historyError,
    sourceLinks,
    openEntries,
    closedEntries,
    hasOpen,
    blockableTagIds,
    focusedCode,
    activityCount,
    notesBadgeCount,
    eyebrow,
    rowSummary,
    rowSubtitleText,
  } = model

  // The Closed tab only earns its place when there's closed history to show -
  // otherwise it's a permanent muted "0" on the common (nothing-resolved) case.
  // If the user reopens the last closed entry while on that tab, fall back to
  // Open so the value never points at a tab that's no longer rendered.
  const showClosed = closedEntries.length > 0
  const effectiveTab: DrawerTab =
    activeTab === 'closed' && !showClosed ? 'open' : activeTab

  // Every tab carries a count badge at all times (0 renders muted grey, not
  // hidden). Plain text labels, no icons - the badge is the signal. Short
  // "Open" / "Closed" labels (the drawer's whole subject is issues). Notes /
  // Activity are Evo/PSC-only - FP users get the issue tab(s) alone. Open +
  // Activity ride eager data; Notes uses the eager bulk-count until its live
  // feed loads (see ``notesBadgeCount``) and emphasizes a non-zero badge so
  // notes never go unnoticed.
  const tabs: AppDrawerTabDef<DrawerTab>[] = [
    { value: 'open', label: 'Open', count: openEntries.length },
    ...(showClosed
      ? [{ value: 'closed', label: 'Closed', count: closedEntries.length } as AppDrawerTabDef<DrawerTab>]
      : []),
    ...(notesGated
      ? ([
          {
            value: 'notes',
            label: 'Notes',
            count: notesBadgeCount,
            emphasizeCount: true,
          },
          { value: 'activity', label: 'Activity', count: activityCount },
        ] as AppDrawerTabDef<DrawerTab>[])
      : []),
  ]

  return (
    <AppDrawerShell
      bg={poolwerxColors.surface.primary}
      sx={
        fpContext
          ? {
              // The admin context bar (first child) owns the close control;
              // hide the shared header's own close so only one X shows.
              '& > :not(:first-of-type) button[aria-label="Close"]': {
                visibility: 'hidden',
              },
            }
          : undefined
      }
    >
      {/* Cross-FP admin surfaces: FP scope + report jump + close, pinned
       * above the (byte-identical) shared header. Inspector leaves
       * ``fpContext`` undefined and renders neither this bar nor a report
       * jump in the body. */}
      {fpContext && (
        <AdminContextBar
          fpContext={fpContext}
          reportHref={reportHref}
          onClose={onClose}
        />
      )}
      <AppDrawerHeader
        eyebrow={eyebrow}
        eyebrowColor={poolwerxColors.text.secondary}
        title={rowSummary || 'Exception row'}
        titleAttr={rowSummary || undefined}
        titleColor={poolwerxColors.teal}
        subtitle={rowSubtitleText}
        belowSubtitle={
          <SourceLinksRow
            links={sourceLinks}
            trailing={
              onCompareSales ? (
                // Same link styling as the source links so the row reads as
                // one action cluster, but no external-link glyph - this opens
                // an in-app dialog, not a new tab (button onClick is the
                // sanctioned pattern for dialogs; see RULES.md Navigation).
                <Link
                  component="button"
                  type="button"
                  onClick={onCompareSales}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '12.5px',
                    fontWeight: 500,
                    color: poolwerxColors.teal,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <BalanceIcon sx={{ fontSize: 15 }} />
                  Compare
                </Link>
              ) : undefined
            }
          />
        }
        onClose={onClose}
      />

      {/* Tab bar - every role gets the Open issue tab (+ Closed when there's
        * history); FP users see just those (Notes / Activity are filtered out
        * of ``tabs`` above). The Evo/PSC bulk-action buttons sit on their own
        * slim row directly beneath the tabs on the Open tab when open issues
        * exist. Gated behind ``bodyReady`` with the body so the whole drawer
        * reveals at once (badge counts are final by then - no flicker). */}
      {bodyReady && (
        <>
          <AppDrawerTabs<DrawerTab>
            tabs={tabs}
            value={effectiveTab}
            onChange={handleTabChange}
          />
          {actionsGated && effectiveTab === 'open' && hasOpen && (
            <ObjectActionLine
              actionableCount={openEntries.length}
              hasBlockable={blockableTagIds.length > 0}
              pending={objectActions.pending}
              onFixAll={objectActions.markAllFixed}
              onCantFix={objectActions.cantFixAll}
            />
          )}
        </>
      )}

      <AppDrawerBody padding={{ p: 0 }}>
        {!bodyReady ? (
          // One continuous skeleton until the row AND every eager fetch
          // (object history, note counts) have settled - the body then
          // renders all-at-once instead of pop-in-as-it-lands.
          <Stack
            spacing={1.5}
            aria-busy="true"
            data-testid="drawer-loading"
            sx={{ px: '22px', pt: '16px' }}
          >
            {[96, 44, 44, 140].map((h, i) => (
              <Skeleton key={i} variant="rounded" height={h} />
            ))}
          </Stack>
        ) : (
          <>
            {/* The active tab's body. Open / Closed issues render for every
              * role; Notes / Activity stay Evo/PSC-only (FP users can't select
              * them - no tab - and the guard is defence-in-depth). */}
            {effectiveTab === 'open' && renderIssuesBody('open')}
            {effectiveTab === 'closed' && renderIssuesBody('closed')}
            {notesGated && effectiveTab === 'activity' && (
              <ActivityPanel
                events={displayEvents}
                loading={historyLoading}
                error={historyError}
                lastConfirmedAt={lastConfirmedAt}
                currentCode={focusedCode}
              />
            )}
            {notesGated && effectiveTab === 'notes' && (
              <Box sx={{ px: '22px', py: '16px' }}>
                <NotesPanel notes={notes} fpCode={fpCode} />
              </Box>
            )}
          </>
        )}
      </AppDrawerBody>

      {/* Footer (Evo/PSC; hidden for FP + while loading). On the Notes tab the
        * composer IS the footer; on Issues/Activity a slim "Add note" jumps to
        * the Notes tab and focuses the composer. */}
      {bodyReady &&
        notesGated &&
        (activeTab === 'notes' ? (
          <NotesComposer notes={notes} user={user} />
        ) : (
          <AppDrawerFooter padding={{ px: 2, py: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditNoteIcon />}
                disabled={!notes.canPost}
                onClick={() => {
                  handleTabChange('notes')
                  notes.handleAddNote()
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  color: poolwerxColors.teal,
                  borderColor: poolwerxColors.teal,
                  '&:hover': {
                    borderColor: poolwerxColors.tealHover,
                    bgcolor: 'rgba(0,127,152,0.04)',
                  },
                }}
              >
                Add note
              </Button>
            </Box>
          </AppDrawerFooter>
        ))}
    </AppDrawerShell>
  )
}

export default ExceptionDrawerPanel
